import React, { useEffect, useState } from 'react';

import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import CloseIcon from '@mui/icons-material/Close';

import { filterActionCreators, filterSelectors } from '~/redux/ducks/playersAndTeams/filter';
import { reportActionCreators } from '~/redux/ducks/playersAndTeams/report';
import { areObjectsDifferent } from '~/redux/ducks/playersAndTeams/sagas/runReportValidation';
import { commonMessages } from '~/utility/commonMessages';

import { findComparisonOperator } from '../customFilters/utils';

import utils from './utils';

import { BreadcrumbsContainer, Breadcrumb, BreadcrumbValue, DeleteBreadcrumb, ClearFilter } from './styles';

const Breadcrumbs = () => {
    const intl = useIntl();
    const dispatch = useDispatch();
    const config = useSelector((state) => state.config);
    const reportCategory = useSelector((state) => state.playersAndTeams.report.reportCategory);
    // We don't have the filterSelectors.getActiveFilters in the use selector b/c this reduces renders
    const playersAndTeamsFilter = useSelector((state) => state.playersAndTeams.filter);
    const activeFilters = filterSelectors.getActiveFilters(playersAndTeamsFilter);

    const [breadcrumbs, setBreadcrumbs] = useState(utils.getBreadcrumbs([], activeFilters));

    useEffect(() => {
        const updatedBreadcrumbs = utils.getBreadcrumbs(breadcrumbs, activeFilters);
        const newBreadcrumbMap = utils.createBreadcrumbMap(updatedBreadcrumbs);
        const oldBreadcrumbMap = utils.createBreadcrumbMap(breadcrumbs);

        // Only update breadcrumbs when theres a difference or we'll constantly be updating breadcrumbs
        if (areObjectsDifferent(newBreadcrumbMap, oldBreadcrumbMap)) {
            setBreadcrumbs(updatedBreadcrumbs);
        }
    }, [activeFilters, breadcrumbs]);

    // Currying so we don't have to wrap the function in an anonymous function in the event handler
    const resetFilter = (key) => () => {
        dispatch(filterActionCreators.resetFilter(key));
        dispatch(reportActionCreators.runReport());
        setBreadcrumbs(breadcrumbs.filter((obj) => !obj[key]));
    };

    const resetCustomFilter = (customFilters, indexToRemove) => () => {
        const remainingFilters = customFilters.filter((_, index) => index !== indexToRemove);
        dispatch(filterActionCreators.setCustomFilters(remainingFilters));
        dispatch(reportActionCreators.runReport());
    };

    const clearFilterHandler = () => {
        dispatch(filterActionCreators.resetAllFilters());
        dispatch(reportActionCreators.runReport());
    };

    // Why this way? so we can maintain the order things were added
    const crumbs = breadcrumbs
        .map((obj) => {
            const [key] = Object.keys(obj);
            const val = obj[key];

            if (key === 'customFilters') {
                const customFilters = val;
                return customFilters.length === 0
                    ? null
                    : customFilters.map((filter, index) => {
                          const formattedTitle = intl.formatMessage(commonMessages.resultFilterValues[filter.measure]);
                          return (
                              <Breadcrumb key={`${filter.measure}${filter.operator}${filter.value}`}>
                                  {formattedTitle}&nbsp;
                                  {findComparisonOperator(filter.operator)}&nbsp;
                                  <BreadcrumbValue>{filter.value}</BreadcrumbValue>&nbsp;
                                  <DeleteBreadcrumb onClick={resetCustomFilter(customFilters, index)}>
                                      <CloseIcon />
                                  </DeleteBreadcrumb>
                              </Breadcrumb>
                          );
                      });
            }

            const formattedTitle = utils.formatTitle(intl, key, reportCategory);
            const formattedValue = utils.formatValueForBreadcrumb(intl, config, key, val);
            return (
                <Breadcrumb key={key}>
                    {formattedTitle}&nbsp;
                    <BreadcrumbValue>{formattedValue}</BreadcrumbValue>&nbsp;
                    <DeleteBreadcrumb onClick={resetFilter(key)}>
                        <CloseIcon />
                    </DeleteBreadcrumb>
                </Breadcrumb>
            );
        })
        .filter(Boolean);

    return crumbs.length === 0 ? null : (
        <BreadcrumbsContainer>
            {crumbs}
            <ClearFilter onClick={clearFilterHandler}>Clear Filters</ClearFilter>
        </BreadcrumbsContainer>
    );
};

export default React.memo(Breadcrumbs);
